import React from 'react';
import { Plus } from 'lucide-react';
import { isoDate } from '../utils/rosterDates';
import RosterShiftChip from './RosterShiftChip';

export default function RosterCalendar({
  track, period, days, byDate, onAdd, onEdit, monthAnchor,
}) {
  const accent = track.accent;
  const isMonth = period === 'month';
  const todayIso = isoDate(new Date());
  const showLoc = !track.bookingAware;

  if (!isMonth) {
    return (
      <div
        className="rounded-2xl border p-2 sm:p-3"
        style={{ background: `${accent}08`, borderColor: `${accent}33` }}
        data-testid="roster-week-grid"
      >
        <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
          {days.map((d) => {
            const iso = isoDate(d);
            const items = byDate[iso] || [];
            const isToday = iso === todayIso;
            return (
              <div
                key={iso}
                className={`rounded-xl border bg-white min-h-[200px] flex flex-col ${isToday ? 'ring-2' : ''}`}
                style={isToday ? { ['--tw-ring-color']: accent } : {}}
              >
                <div className="px-2.5 py-2 border-b flex items-center justify-between"
                  style={{ borderBottomColor: `${accent}22` }}>
                  <div>
                    <p className="text-[10px] uppercase text-zinc-500">
                      {d.toLocaleDateString('en-IN', { weekday: 'short', timeZone: 'Asia/Kolkata' })}
                    </p>
                    <p className="text-sm font-bold text-zinc-900">{d.getDate()}</p>
                  </div>
                  <button type="button" onClick={() => onAdd(iso)}
                    className="h-7 w-7 rounded-full flex items-center justify-center text-white"
                    style={{ background: accent }} aria-label={`Add shift on ${iso}`}
                    data-testid={`roster-add-${iso}`}>
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="p-1.5 space-y-1.5 flex-1 overflow-y-auto">
                  {items.length === 0 && (
                    <p className="text-[10px] text-zinc-400 text-center py-6 px-1">{track.emptyHint}</p>
                  )}
                  {items.map((e) => (
                    <RosterShiftChip key={e.id} entry={e} accent={accent} onClick={onEdit} showLocation={showLoc} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const month = monthAnchor.getMonth();

  return (
    <div
      className="rounded-2xl border p-2 sm:p-3"
      style={{ background: `${accent}08`, borderColor: `${accent}33` }}
      data-testid="roster-month-grid"
    >
      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdays.map((w) => (
          <div key={w} className="text-[10px] uppercase tracking-wide text-zinc-500 text-center py-1">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const iso = isoDate(d);
          const items = byDate[iso] || [];
          const inMonth = d.getMonth() === month;
          const isToday = iso === todayIso;
          return (
            <div
              key={iso}
              className={`rounded-lg border min-h-[96px] p-1.5 flex flex-col ${
                inMonth ? 'bg-white' : 'bg-zinc-50/80'
              } ${isToday ? 'ring-2' : ''}`}
              style={isToday ? { ['--tw-ring-color']: accent } : {}}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-semibold ${inMonth ? 'text-zinc-800' : 'text-zinc-400'}`}>
                  {d.getDate()}
                </span>
                {inMonth && (
                  <button type="button" onClick={() => onAdd(iso)} className="text-zinc-400 hover:text-zinc-700">
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="space-y-1 overflow-hidden">
                {items.slice(0, 3).map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => onEdit(e)}
                    className={`w-full text-left text-[9px] pl-1.5 pr-1 py-0.5 rounded truncate border-l-2 ${
                      e.published ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                    }`}
                    style={{ borderLeftColor: accent }}
                  >
                    {e.shift_start} {e.staff_name?.split(' ').slice(-1)[0]}
                  </button>
                ))}
                {items.length > 3 && (
                  <p className="text-[9px] text-zinc-500 px-1">+{items.length - 3} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
